import { useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import IPButton from '../../../../components/IPButton'
import IPInput from '../../../../components/IPInput'
import IPSelectBox from '../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../components/IPSelectBox/IPSelectBox'
import InputCampaign from '../../../../components/InputCampaign'
import { BaseComponentWithContextProps } from '../../../../base/customHooks/componentHOC'
import { IException } from '../../../../base/models'
import { DateInputValue, toApiDate, toDateInputValue, validateDateRange } from '../../../MySpecial/AppleDep/models/dateInput'
import {
  appleDepStatusClass,
  AppleDepOrderTypeLabels,
  AppleDepStatusLabels,
  IAppleDepDevice,
  IAppleDepEnvironment,
} from '../../../MySpecial/AppleDep/models/models'
import { IAppleDepShowOrderDetailsResponse } from '../models/models'
import {
  AdminGetDevicesApi,
  AdminSendToAppleApi,
  AdminShowOrderDetailsApi,
} from '../services/appleDepAdminApi'

interface DeviceOperationsTabProps extends BaseComponentWithContextProps {
  environment?: IAppleDepEnvironment
}

const startOfYear = () => {
  const date = new Date()
  date.setMonth(0, 1)
  date.setHours(0, 0, 0, 0)
  return date
}


/** Apple'ın kısıtlı ekipte olmasını şart koştuğu işlem tipleri. */
const restrictedOrderTypes: { value: string; label: string; description: string }[] = [
  {
    value: 'RE',
    label: AppleDepOrderTypeLabels.RE,
    description:
      "Cihazı müşterinin ABM hesabından çıkarır. Apple, sipariş numarasının sonuna otomatik olarak \"Return\" ekler. RE gönderilmeyen iade cihaz eski sahibinde asılı kalır ve başka bir ABM hesabına asla atanamaz.",
  },
  {
    value: 'OV',
    label: AppleDepOrderTypeLabels.OV,
    description:
      'Hatalı Organization ID, tarih veya cihaz bilgisinin düzeltilmesi için kullanılır. Cihazın daha önce Apple\'a gönderilmiş olması gerekir.',
  },
  {
    value: 'VD',
    label: AppleDepOrderTypeLabels.VD,
    description:
      "Siparişin tümünü iptal eder. Apple bu tipte cihaz/sevkiyat bilgisi beklemez; yalnızca sipariş başlığı gönderilir.",
  },
]

/**
 * Index'in bayi adına yaptığı iade (RE), düzeltme (OV) ve iptal (VD) işlemleri.
 * Bayi ekranı yalnızca OR gönderebilir; bu tipler Apple'ın şartı gereği burada.
 */
export default function DeviceOperationsTab(props: DeviceOperationsTabProps) {
  const [dealerCode, setDealerCode] = useState('')
  const [startDate, setStartDate] = useState<DateInputValue>(startOfYear())
  const [endDate, setEndDate] = useState<DateInputValue>(new Date())
  const [serialNumber, setSerialNumber] = useState('')

  const [devices, setDevices] = useState<IAppleDepDevice[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [orderType, setOrderType] = useState('RE')

  const [orderNumbersText, setOrderNumbersText] = useState('')
  const [showOrderResult, setShowOrderResult] = useState<IAppleDepShowOrderDetailsResponse | undefined>(undefined)

  const showError = (err: any, fallback = 'İşlem sırasında bir hata oluştu.') => {
    const exception = err as IException
    Swal.fire({
      icon: 'error',
      title: exception?.description ?? fallback,
      showConfirmButton: true,
      allowOutsideClick: false,
    })
  }

  const loadDevices = async () => {
    if (!dealerCode.trim()) {
      Swal.fire({ icon: 'warning', title: 'Bayi kodu zorunludur.' })
      return
    }

    const dateError = validateDateRange(startDate, endDate)
    if (dateError) {
      Swal.fire({ icon: 'warning', title: dateError })
      return
    }

    props.showLoading()
    try {
      const api: AdminGetDevicesApi = new AdminGetDevicesApi({})
      const result = await api.getDevices({
        DealerCode: dealerCode.trim(),
        StartDate: toApiDate(startDate),
        EndDate: toApiDate(endDate),
        SerialNumber: serialNumber,
        // Admin ekranında SAP'ten yeniden çekmiyoruz; mevcut kayıtlar üzerinde çalışılır.
        RefreshFromSap: false,
      })
      setDevices(result ?? [])
      setSelectedIds([])
    } catch (err: any) {
      showError(err, 'Cihaz listesi alınamadı.')
    } finally {
      props.hideLoading()
    }
  }

  const toggleDevice = (id: number) => {
    setSelectedIds(current => (current.includes(id) ? current.filter(x => x !== id) : [...current, id]))
  }

  const selectedOrderType = useMemo(
    () => restrictedOrderTypes.find(o => o.value === orderType),
    [orderType]
  )

  const send = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Önce cihaz seçin.' })
      return
    }

    const isLive = props.environment?.IsLive === 1
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: `${selectedOrderType?.label} — ${selectedIds.length} cihaz`,
      html: `
        <div style="text-align:left">
          <p><strong>Bayi:</strong> ${dealerCode}</p>
          <p><strong>Ortam:</strong> ${isLive ? 'CANLI' : 'TEST'}</p>
          <p style="margin-top:10px">${selectedOrderType?.description ?? ''}</p>
        </div>`,
      showCancelButton: true,
      confirmButtonText: `${orderType} Gönder`,
      cancelButtonText: 'Vazgeç',
    })

    if (!confirmation.isConfirmed) return

    props.showLoading()
    try {
      const api: AdminSendToAppleApi = new AdminSendToAppleApi({})
      const result = await api.sendToApple({
        DeviceIds: selectedIds,
        OrderType: orderType,
        DealerCode: dealerCode.trim(),
      })

      await loadDevices()

      const skipped = result?.SkippedDevices ?? []
      Swal.fire({
        icon: 'success',
        title: 'Gönderim yapıldı',
        html: `
          <div style="text-align:left">
            <p><strong>İşlem No:</strong> ${result?.TransactionId ?? '-'}</p>
            <p><strong>Apple İşlem No:</strong> ${result?.DeviceEnrollmentTransactionId ?? 'bekleniyor'}</p>
            <p><strong>Gönderilen cihaz:</strong> ${result?.SentDeviceCount ?? 0}</p>
            ${
              skipped.length
                ? `<p><strong>Atlanan:</strong><br/>${skipped
                    .map(s => `${s.SerialNumber}: ${s.Reason}`)
                    .join('<br/>')}</p>`
                : ''
            }
          </div>`,
      })
    } catch (err: any) {
      showError(err, 'Gönderim başarısız oldu.')
    } finally {
      props.hideLoading()
    }
  }

  const showOrderDetails = async () => {
    const orderNumbers = orderNumbersText
      .split(/[\s,;]+/)
      .map(x => x.trim())
      .filter(x => x.length > 0)

    if (!dealerCode.trim()) {
      Swal.fire({ icon: 'warning', title: 'Bayi kodu zorunludur.' })
      return
    }

    if (orderNumbers.length === 0) {
      Swal.fire({ icon: 'warning', title: 'En az bir sipariş numarası girin.' })
      return
    }

    props.showLoading()
    try {
      const api: AdminShowOrderDetailsApi = new AdminShowOrderDetailsApi({})
      const result = await api.showOrderDetails({ OrderNumbers: orderNumbers, DealerCode: dealerCode.trim() })
      setShowOrderResult(result)

      if (!result?.orders?.length) {
        Swal.fire({
          icon: 'info',
          title: 'Kayıt bulunamadı',
          text: 'Apple yalnızca tamamlanmış siparişleri döndürür; devam eden gönderimler yansımaz.',
        })
      }
    } catch (err: any) {
      showError(err, 'Sipariş detayı alınamadı.')
    } finally {
      props.hideLoading()
    }
  }

  const orderTypeOptions: ISelectOption[] = restrictedOrderTypes.map(o => ({ value: o.value, label: o.label }))

  return (
    <div className='appleDepOperations'>
      <div className='appleDepWarning'>
        <strong>Dikkat.</strong> Bu sekmedeki işlemler Apple'daki kayıtları geri alır veya değiştirir.
        Apple, iade / düzeltme / iptal erişiminin yalnızca eğitimli ve kısıtlı bir ekipte olmasını şart
        koşuyor. Yapılan her işlem kullanıcı, tarih, önceki ve yeni değerle birlikte denetim kaydına yazılır.
      </div>

      <div className='appleDepFilters'>
        <div className='row'>
          <div className='col-12 col-md-2 mb-2'>
            <label className='labeltext'>Bayi Kodu *</label>
            <IPInput type='text' value={dealerCode} onChange={e => setDealerCode(e.target.value)} />
          </div>
          <div className='col-12 col-md-2 mb-2'>
            <InputCampaign
              type='date'
              id='OpsStartDate'
              name='OpsStartDate'
              label='Fatura Başlangıç'
              value={toDateInputValue(startDate)}
              onChange={e => setStartDate(e.target.value)}
              isActive
            />
          </div>
          <div className='col-12 col-md-2 mb-2'>
            <InputCampaign
              type='date'
              id='OpsEndDate'
              name='OpsEndDate'
              label='Fatura Bitiş'
              value={toDateInputValue(endDate)}
              onChange={e => setEndDate(e.target.value)}
              isActive
            />
          </div>
          <div className='col-12 col-md-2 mb-2'>
            <label className='labeltext'>Seri No / IMEI</label>
            <IPInput type='text' value={serialNumber} onChange={e => setSerialNumber(e.target.value)} />
          </div>
          <div className='col-12 col-md-2 mb-2 appleDepFilterAction'>
            <IPButton text='Listele' className='appleDepPrimaryButton' onClick={loadDevices} />
          </div>
        </div>
      </div>

      <div className='appleDepToolbar'>
        <div className='appleDepToolbarInfo'>
          {devices.length} cihaz, <strong>{selectedIds.length}</strong> seçili.
          {selectedOrderType && <div className='appleDepBlockReason'>{selectedOrderType.description}</div>}
        </div>
        <div className='appleDepToolbarButtons'>
          <div style={{ minWidth: 200 }}>
            <IPSelectBox
              options={orderTypeOptions}
              value={orderTypeOptions.find(o => o.value === orderType)}
              onChangeSingle={(option: ISelectOption) => setOrderType(option ? `${option.value}` : 'RE')}
            />
          </div>
          <IPButton text={`${orderType} Gönder`} className='appleDepPrimaryButton' onClick={send} />
        </div>
      </div>

      {devices.length === 0 ? (
        <div className='appleDepEmpty'>Bayi kodu girip Listele'ye basın.</div>
      ) : (
        <div className='appleDepTableWrapper'>
          <table className='table table-hover appleDepTable'>
            <thead>
              <tr>
                <th className='appleDepCheckCol'></th>
                <th>Seri No / IMEI</th>
                <th>Ürün</th>
                <th>Sipariş No</th>
                <th>Teslimat No</th>
                <th>Fatura No</th>
                <th>Müşteri</th>
                <th>Organization ID</th>
                <th>Son İşlem</th>
                <th>Durum</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(device => (
                <tr key={device.Id} className={selectedIds.includes(device.Id) ? 'appleDepRowSelected' : ''}>
                  <td className='appleDepCheckCol'>
                    <input
                      type='checkbox'
                      checked={selectedIds.includes(device.Id)}
                      onChange={() => toggleDevice(device.Id)}
                    />
                  </td>
                  <td>
                    <strong>{device.SerialNumber}</strong>
                  </td>
                  <td>
                    <div className='appleDepProductName'>{device.ProductDescription}</div>
                    <small>{device.ProductCode}</small>
                  </td>
                  <td>
                    <strong>{device.SapOrderNo ?? '-'}</strong>
                  </td>
                  <td>{device.SapDeliveryNo ?? '-'}</td>
                  <td>{device.SapInvoiceNo}</td>
                  <td>{device.CustomerName ?? '-'}</td>
                  <td>{device.OrganizationId ?? '-'}</td>
                  <td>
                    {device.LastSentOrderType
                      ? AppleDepOrderTypeLabels[device.LastSentOrderType] ?? device.LastSentOrderType
                      : '-'}
                  </td>
                  <td>
                    <span className={appleDepStatusClass(device.Status)}>
                      {AppleDepStatusLabels[device.Status] ?? device.Status}
                    </span>
                    {device.StatusMessage && <div className='appleDepBlockReason'>{device.StatusMessage}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Show Order Details — denetim amaçlı */}
      <div className='appleDepShowOrder'>
        <h5>Show Order Details</h5>
        <p className='appleDepToolbarInfo'>
          Apple'da o an kayıtlı cihazları sipariş numarasına göre sorgular. Yalnızca tamamlanmış
          siparişler döner; devam eden gönderimler yansımaz. Apple'ın compliance listesinde bu API'nin
          uygulanması zorunlu.
        </p>
        <div className='row'>
          <div className='col-12 col-md-6 mb-2'>
            <label className='labeltext'>Sipariş Numaraları (virgül veya boşlukla ayırın)</label>
            <IPInput
              type='text'
              value={orderNumbersText}
              onChange={e => setOrderNumbersText(e.target.value)}
              placeholder='ORDER_900123, ORDER_900124'
            />
          </div>
          <div className='col-12 col-md-3 mb-2 appleDepFilterAction'>
            <IPButton text='Apple’a Sor' className='appleDepSecondaryButton' onClick={showOrderDetails} />
          </div>
        </div>

        {showOrderResult?.orders?.length ? (
          <div className='appleDepTableWrapper'>
            <table className='table table-hover appleDepTable'>
              <thead>
                <tr>
                  <th>Sipariş No</th>
                  <th>Tip</th>
                  <th>Organization ID</th>
                  <th>Sevkiyat</th>
                  <th>Apple'da Kayıtlı Cihazlar</th>
                  <th>Statü</th>
                </tr>
              </thead>
              <tbody>
                {showOrderResult.orders.map((order, orderIndex) =>
                  (order.deliveries?.length ? order.deliveries : [undefined]).map((delivery, deliveryIndex) => (
                    <tr key={`${orderIndex}-${deliveryIndex}`}>
                      <td>
                        <strong>{order.orderNumber}</strong>
                      </td>
                      <td>{order.orderType}</td>
                      <td>{order.customerId}</td>
                      <td>{delivery?.deliveryNumber ?? '-'}</td>
                      <td>
                        {delivery?.devices?.length
                          ? delivery.devices.map(device => device.deviceId).join(', ')
                          : '-'}
                      </td>
                      <td>
                        <span className='appleDepBadge appleDepBadgeInfo'>
                          {order.showOrderStatusCode ?? showOrderResult.statusCode ?? '-'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  )
}
